import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import styles from "./AgendaVotingOptions.module.css";
import { t } from "i18next";
import { VoteType } from "src/stores/assemblies/VotingStore";

const MAX_INPUTS = 5;
const ARBITRAMENT_OPTIONS = () => [
    t("labels.yes").toString(),
    t("labels.no").toString(),
    t("labels.abstrain").toString()
];
const SELECTION_OPTIONS = () => [
    `${t("labels.option")} A`,
    `${t("labels.option")} B`,
    `${t("labels.option")} C`,
    "",
    ""
];

const mapOptionsForExistingVote = (options: string[]) => options.map((text) => ({ votingoptionid: "", text }));

const SelectionList = observer(function SelectionList() {
    const { votingStore, assemblyStore } = useContext(RootStoreContext);
    const isLocked = assemblyStore.isAssemblyReadOnly;
    const options = votingStore.isNewVoting
        ? votingStore.votingOptionsDefault
        : votingStore.votingOptions.map((o) => o.text);

    const updateOption = useCallback(
        (index: number, value: string) => {
            if (votingStore.isNewVoting) {
                const updated = [...votingStore.votingOptionsDefault];
                updated[index] = value;
                votingStore.setVotingOptionsDefault(updated);
            } else {
                const updated = [...votingStore.votingOptions];
                updated[index] = { ...updated[index], text: value };
                votingStore.setVotingOptions(updated);
            }
        },
        [votingStore]
    );

    const removeOption = useCallback(
        (index: number) => {
            if (votingStore.isNewVoting) {
                const updated = votingStore.votingOptionsDefault.filter((_, i) => i !== index);
                votingStore.setVotingOptionsDefault(updated);
            } else {
                const updated = votingStore.votingOptions.filter((_, i) => i !== index);
                votingStore.setVotingOptions(updated);
            }
        },
        [votingStore]
    );

    const handlers = useMemo(
        () =>
            options.map((_, index) => ({
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => updateOption(index, e.target.value),
                onClick: () => removeOption(index)
            })),
        [updateOption, removeOption, options]
    );

    const isArbitrament = votingStore.votingBasis.votetype === VoteType.ARBITRAMENT;

    return (
        <>
            {options.map((option, index) => (
                <div key={index} className={styles.inputRow}>
                    <input
                        className={isArbitrament || isLocked ? styles.inputReadOnly : styles.input}
                        value={option}
                        onChange={handlers[index].onChange}
                        placeholder={t("labels.placeholder").toString()}
                        readOnly={isArbitrament || isLocked}
                    />
                    {isArbitrament || isLocked ? null : (
                        <UI.Icon
                            icon={UI.SVGIcon.Close}
                            size={20}
                            className={styles.removeButton}
                            onClick={handlers[index].onClick}
                        />
                    )}
                </div>
            ))}
        </>
    );
});

const SelectionListAddButton = observer(function SelectionListAddButton() {
    const { t } = useTranslation();
    const { votingStore, assemblyStore } = useContext(RootStoreContext);

    const isNew = votingStore.isNewVoting;
    const options = isNew ? votingStore.votingOptionsDefault : votingStore.votingOptions.map((o) => o.text);
    const canAdd = options.length < MAX_INPUTS;

    const addOption = useCallback(() => {
        if (isNew) {
            const updated = [...votingStore.votingOptionsDefault, ""];
            votingStore.setVotingOptionsDefault(updated);
        } else {
            const updated = [...votingStore.votingOptions, { votingoptionid: "", text: "" }];
            votingStore.setVotingOptions(updated);
        }
    }, [votingStore, isNew]);

    const isArbitrament = votingStore.votingBasis.votetype === VoteType.ARBITRAMENT;

    if (isArbitrament || assemblyStore.isAssemblyReadOnly) {
        return null; // Don't show add button for arbitrament voting or when the agenda item is locked
    }

    if (canAdd) {
        return (
            <div className={styles.inputRow}>
                <div className={styles.addButton} onClick={addOption}>
                    <UI.Icon icon={UI.SVGIcon.Add} size={12} className={styles.plus} />
                    {t("labels.add")}
                </div>
            </div>
        );
    }
    return null;
});

const RealestateCheckboxes = observer(function RealestateCheckboxes() {
    const { assemblyStore, votingStore } = useContext(RootStoreContext);

    const onCheckboxChange = useCallback(
        (realestateId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const selected = votingStore.selectedRealestateIds;
            const updated = e.target.checked
                ? [...selected, realestateId] // add if checked
                : selected.filter((id) => id !== realestateId); // remove if unchecked

            votingStore.setSelectedRealestateIds(updated);
        },
        [votingStore]
    );

    return (
        <>
            {assemblyStore.currentAssemblyRealestates.map((realestate) => {
                return (
                    <UI.Checkbox
                        key={realestate.realestate.id}
                        name={realestate.realestate.id}
                        label={realestate.realestate.name}
                        checked={votingStore.selectedRealestateIds.includes(realestate.realestate.id)}
                        onChange={onCheckboxChange(realestate.realestate.id)}
                        disabled={assemblyStore.isAssemblyReadOnly}
                    />
                );
            })}
        </>
    );
});

const ArbitramentSwitch = observer(function ArbitramentSwitch() {
    const { votingStore, assemblyStore } = useContext(RootStoreContext);
    const selectionOptionsRef = useRef<{ votingoptionid: string; text: string }[] | null>(null);

    const toggleArbitrament = useCallback(() => {
        if (assemblyStore.isAssemblyReadOnly) return;
        const isCurrentlyArbitrament = votingStore.votingBasis.votetype === VoteType.ARBITRAMENT;
        const nextVoteType =
            votingStore.votingBasis.votetype === VoteType.ARBITRAMENT ? VoteType.SELECTION_VOTE : VoteType.ARBITRAMENT;

        votingStore.updateVotingBasisField("votetype", nextVoteType);

        if (votingStore.isNewVoting) {
            const nextOptions = nextVoteType === VoteType.ARBITRAMENT ? ARBITRAMENT_OPTIONS() : SELECTION_OPTIONS();
            votingStore.setVotingOptionsDefault(nextOptions);
            return;
        }

        if (!isCurrentlyArbitrament) {
            selectionOptionsRef.current = votingStore.votingOptions.map((option) => ({ ...option }));
            votingStore.setVotingOptions(mapOptionsForExistingVote(ARBITRAMENT_OPTIONS()));
            return;
        }

        votingStore.setVotingOptions(selectionOptionsRef.current ?? mapOptionsForExistingVote(SELECTION_OPTIONS()));
    }, [votingStore, assemblyStore]);

    const isArbitrament = votingStore.votingBasis.votetype === VoteType.ARBITRAMENT;

    return (
        <div className={styles.switchCard}>
            <div className={isArbitrament ? styles.switchOptionInactive : styles.switchOptionActive}>
                {t("screens.assemblies.vote.selection_vote").toString()}
            </div>
            <UI.Switch checked={isArbitrament} onToggle={toggleArbitrament} disabled={assemblyStore.isAssemblyReadOnly} />
            <div className={isArbitrament ? styles.switchOptionActive : styles.switchOptionInactive}>
                {t("screens.assemblies.vote.arbitrament").toString()}
            </div>
        </div>
    );
});

export const AgendaVotingOptions = observer(function AgendaVotingOptions() {
    const { t } = useTranslation();
    const { assemblyStore, votingStore } = useContext(RootStoreContext);

    useEffect(() => {
        if (assemblyStore.currentAssemblyRealestates.length > 0 && votingStore.selectedRealestateIds.length === 0) {
            const allIds = assemblyStore.currentAssemblyRealestates.map((re) => re.realestate.id);
            votingStore.setSelectedRealestateIds(allIds);
        }
    }, [assemblyStore.currentAssemblyRealestates, votingStore]);

    return (
        <>
            <UI.Col>
                <ArbitramentSwitch />
            </UI.Col>
            <UI.Col>
                <div className={styles.selectionTitle}>{t("labels.selection")}</div>
                <SelectionList />
                <SelectionListAddButton />
            </UI.Col>
            <UI.Col>
                <div className={styles.checkboxCard}>
                    <div className={styles.checkboxTitle}>{t("labels.communion")}</div>
                    <div className={styles.checkboxGroup}>
                        <RealestateCheckboxes />
                    </div>
                </div>
            </UI.Col>
        </>
    );
});
